import Keycloak from 'keycloak-js';
import { appConfig } from '../config/appConfig';

// Initialize Keycloak instance with per-app configuration
// Use unique storageKey to isolate sessions between different frontend apps
// This prevents auto-login when both apps are open in the same browser
// Note: storageKey is not in the official KeycloakConfig type but is supported at runtime
// We use type assertion to bypass TypeScript checking while maintaining functionality
const keycloakConfig = {
  url: appConfig.keycloak.url,
  realm: appConfig.keycloak.realm,
  clientId: appConfig.keycloak.clientId,
  // Use clientId as storageKey to ensure each app has isolated Keycloak session storage
  // This prevents session sharing between app1 and app2 when both are open
  // storageKey is a runtime property supported by keycloak-js but not in TypeScript definitions
  storageKey: `keycloak-${appConfig.keycloak.clientId}`,
} as any; // Type assertion to allow storageKey property

const keycloak = new Keycloak(keycloakConfig);

// Initialize Keycloak
export const initKeycloak = (): Promise<boolean> => {
  return new Promise((resolve, reject) => {
    keycloak
      .init({
        onLoad: 'login-required', // or 'check-sso' for silent check
        checkLoginIframe: false,
        pkceMethod: 'S256',
      })
      .then((authenticated: boolean) => {
        if (authenticated) {
          // Token refresh every 5 minutes
          setInterval(() => {
            keycloak.updateToken(70).catch(() => {
              console.error('Failed to refresh token');
            });
          }, 60000);
        }
        resolve(authenticated);
      })
      .catch((error: unknown) => {
        console.error('Keycloak initialization failed:', error);
        reject(error);
      });
  });
};

// Get the Keycloak instance
export const getKeycloak = () => keycloak;

// Get the access token (with automatic refresh if needed)
export const getToken = async (): Promise<string | undefined> => {
  if (!keycloak.authenticated) {
    console.warn('[Keycloak] User not authenticated, no token available');
    return undefined;
  }
  
  try {
    // Check if token needs refresh (within 70 seconds of expiry)
    const refreshed = await keycloak.updateToken(70);
    if (refreshed) {
      console.log('[Keycloak] Token refreshed successfully');
    }
  } catch (error) {
    console.error('[Keycloak] Failed to refresh token:', error);
    // Continue with existing token if refresh fails
  }
  
  return keycloak.token || undefined;
};

// Get user info
// Note: For email-based Keycloak login, preferred_username may be empty
// In that case, we use email as the username
export const getUserInfo = () => {
  if (!keycloak.authenticated) {
    return null;
  }

  // Get username with email fallback (for email-based Keycloak login)
  const username = keycloak.tokenParsed?.preferred_username 
    || keycloak.tokenParsed?.name 
    || keycloak.tokenParsed?.email;

  return {
    id: keycloak.tokenParsed?.sub,
    username: username,
    email: keycloak.tokenParsed?.email,
    firstName: keycloak.tokenParsed?.given_name,
    lastName: keycloak.tokenParsed?.family_name,
  };
};

// Logout
export const logout = () => {
  keycloak.logout();
};

// Check if user is authenticated
export const isAuthenticated = (): boolean => {
  return keycloak.authenticated || false;
};

export default keycloak;

